/**
 * Physical storage spaces for the pantry redesign.
 * Data values are English; UI labels come from i18n (pantry.zones.*).
 */
export type PantryLocation = 'fridge' | 'freezer' | 'counter' | 'pantry';

export const PANTRY_LOCATIONS: PantryLocation[] = ['fridge', 'freezer', 'counter', 'pantry'];

const FREEZER_KEYWORDS = [
  'frozen', 'surgelé', 'congelé', 'glacé', 'ice cream', 'crème glacée', 'popsicle',
];

// Produce that keeps at room temperature + fresh bread. Word-boundary matched.
const COUNTER_KEYWORDS = [
  'banana', 'banane', 'avocado', 'avocat', 'tomato', 'tomate', 'onion', 'oignon',
  'garlic', 'ail', 'potato', 'patate', 'pomme de terre', 'lemon', 'citron', 'lime',
  'bread', 'pain', 'baguette', 'bagel', 'croissant', 'muffin', 'brioche',
];

/**
 * Preserved or sealed goods keep on a shelf regardless of what is inside them.
 * Without this a tin of sardines is filed as Meat and sent to the fridge.
 */
const SHELF_STABLE_KEYWORDS = [
  'en conserve', 'conserve', 'canned', 'boîte de conserve', 'tinned',
  'sous vide', 'déshydraté', 'dehydrated', 'séché', 'dried', 'en poudre',
];

/**
 * Cooked food the user made, not an ingredient they bought (#663).
 *
 * Load-bearing in three places that are easy to miss: the pantry clear spares
 * it, and both the recipe matcher and the grocery aggregator must ignore it —
 * "Restes — Pad thaï" is a meal, and letting it satisfy a recipe's ingredient
 * or cancel a shopping line is how you end up not buying chicken.
 */
export const LEFTOVER_CATEGORY = 'Leftovers';

/**
 * A dish the user cooked and kept, as opposed to a prepared meal they bought.
 *
 * The category alone is NOT the test. `detectCategoryFromName` files anything
 * matching "repas préparé" / "plat préparé" under Leftovers, so a supermarket
 * "Repas préparé Kirkland (surgelé)" lands there too — and that IS an
 * ingredient-like product the recipe matcher and grocery list should keep
 * seeing. Only rows written by the cook-capture flow carry `source: 'leftover'`,
 * so that is what the guards key on.
 */
export const isCookedLeftover = (item: { source?: string }): boolean =>
  item.source === 'leftover';

const PANTRY_CATEGORIES = ['Condiments', 'Spices', 'Packaged Foods', 'Other'];
const FRIDGE_CATEGORIES = ['Meat', 'Dairy', LEFTOVER_CATEGORY, 'Beverages', 'Fruits', 'Vegetables', 'Plant-Based Protein'];

/** Strip accents so "surgelé" also matches a name typed or OCR'd as "surgele". */
const deaccent = (text: string): string =>
  text.replace(/œ/gi, 'oe').replace(/æ/gi, 'ae').normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Word-boundary check tolerant of accents and plural/feminine endings.
 * "Crèmes glacées" has to meet the keyword "crème glacée", so every word in a
 * multi-word keyword gets its own optional `s`/`e` tail.
 */
export const hasKeyword = (text: string, keyword: string): boolean => {
  const pattern = deaccent(keyword)
    .split(/\s+/)
    .map((word) => `${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}e?s?`)
    .join('\\s+');
  return new RegExp(`(^|[^a-z])${pattern}([^a-z]|$)`, 'i').test(deaccent(text));
};

export interface InferredLocation {
  location: PantryLocation;
  /**
   * False when nothing in the name or category actually decided this and
   * 'pantry' is only the fallback. Treating that as an answer is how a fish
   * fillet ends up on the shelf, so callers should route it to review instead.
   */
  confident: boolean;
}

/**
 * Work out where an item lives, and say whether the answer is real.
 *
 * @param category - detected or AI-assigned category
 * @param name - the item name, in either supported language
 * @param categoryConfident - false when `category` is the 'Other' fallback
 *   rather than a real detection; an unconfident 'Other' must not silently
 *   inherit the pantry shelf.
 * @param learnedZone - where the user has already said they keep this food.
 *   Nothing here can outrank that: the rules below are guesses about kitchens
 *   in general, and this is a fact about theirs.
 */
export function inferLocationConfident(
  category: string,
  name: string,
  categoryConfident = true,
  learnedZone?: PantryLocation
): InferredLocation {
  const n = name || '';

  if (learnedZone) return { location: learnedZone, confident: true };

  // Frozen beats everything: the word is a direct statement about storage.
  if (FREEZER_KEYWORDS.some((k) => hasKeyword(n, k))) {
    return { location: 'freezer', confident: true };
  }

  // Preserved goods sit on a shelf whatever their contents.
  if (SHELF_STABLE_KEYWORDS.some((k) => hasKeyword(n, k))) {
    return { location: 'pantry', confident: true };
  }

  // An unrecognised name is not evidence for the shelf — say so.
  if (!categoryConfident && category === 'Other') {
    return { location: 'pantry', confident: false };
  }

  if (PANTRY_CATEGORIES.includes(category)) {
    return { location: 'pantry', confident: category !== 'Other' };
  }

  // For Bread/Grains, Fruits, and Vegetables: check if they're counter items
  if (['Bread/Grains', 'Fruits', 'Vegetables'].includes(category) && COUNTER_KEYWORDS.some((k) => hasKeyword(n, k))) {
    return { location: 'counter', confident: true };
  }

  // Bread/Grains that aren't bread items go to pantry
  if (category === 'Bread/Grains') return { location: 'pantry', confident: true };

  if (FRIDGE_CATEGORIES.includes(category)) return { location: 'fridge', confident: true };

  return { location: 'pantry', confident: false };
}

/**
 * Best-guess storage space.
 *
 * Prefer `inferLocationConfident` when you need to tell a real answer from the
 * fallback — this wrapper cannot express "I don't know".
 */
export function inferLocation(category: string, name: string): PantryLocation {
  return inferLocationConfident(category, name).location;
}

/** Ensure an item has a location (used on rehydrate, cloud sync, and adds). */
export function withLocation<T extends { category: string; name: string; location?: PantryLocation }>(item: T): T {
  if (item.location) return item;
  return { ...item, location: inferLocation(item.category, item.name) };
}
